import { ScrollAreaEnum } from "../../enums";
import { RydeUiUnit } from "../../models";
import { IScrollPositionEntry } from "./scroll-position-memory.models";

/**
 * Module-level so an offset survives the scroll container being unmounted and rebuilt by a refresh.
 * Deliberately not persisted: the offset belongs to the current page visit only, so a reload or a
 * return to the page starts at the top.
 */
const scrollPositionByArea = new Map<ScrollAreaEnum, IScrollPositionEntry>();

/** What the area was scrolled to on this page, or undefined when nothing is remembered for it. */
export const getRememberedScrollPosition = ({
  area,
  pageKey,
}: {
  area: ScrollAreaEnum;
  pageKey: RydeUiUnit;
}): IScrollPositionEntry | undefined => {
  const entry = scrollPositionByArea.get(area);

  // An offset another page left behind is not an answer for this one: areas are shared components
  // (every grid page renders the same one), so the page has to match before it may be restored.
  return entry?.pageKey === pageKey ? entry : undefined;
};

/** Remember where the area is scrolled to, so the next refresh can put it back. */
export const rememberScrollPosition = ({
  area,
  pageKey,
  scrollTop,
  scrollLeft,
}: {
  area: ScrollAreaEnum;
  pageKey: RydeUiUnit;
  scrollTop: number;
  scrollLeft: number;
}): void => {
  scrollPositionByArea.set(area, { pageKey, scrollTop, scrollLeft });
};

/**
 * Forget one area's offset. A scroll the user makes while a refresh is in flight out-ranks the
 * offset recorded before it, and dropping the entry is what lets the restore leave them alone.
 */
export const forgetScrollPosition = ({
  area,
  pageKey,
}: {
  area: ScrollAreaEnum;
  pageKey: RydeUiUnit;
}): void => {
  if (scrollPositionByArea.get(area)?.pageKey === pageKey) {
    scrollPositionByArea.delete(area);
  }
};

/** Drop everything remembered. Called when the user navigates off the page. */
export const clearAllScrollPositions = (): void => {
  scrollPositionByArea.clear();
};
